import { useEffect } from 'react';
import { useTenantBranding } from '../hooks/useTenantBranding';

/**
 * TenantBrandingInitializer Component
 *
 * Injects tenant theme CSS into the DOM to override default index.css colors.
 * Only injects CSS if TenantThemeCSS setting is not empty.
 *
 * This component should be placed early in the component tree (in App.tsx)
 * to ensure theme CSS is applied before components render.
 *
 * @example
 * ```tsx
 * <AbpPermissionsProvider>
 *   <TenantBrandingInitializer />
 *   <YourApp />
 * </AbpPermissionsProvider>
 * ```
 */
export function TenantBrandingInitializer() {
  const { themeCSS } = useTenantBranding();

  useEffect(() => {
    // Only inject CSS if tenant has custom theme CSS
    if (!themeCSS || themeCSS.trim() === '') {
      // Remove any existing tenant theme CSS if tenant theme was cleared
      const existingStyle = document.getElementById('tenant-theme-css');
      if (existingStyle) {
        existingStyle.remove();
      }
      return;
    }

    // Create or update <style> element with tenant theme CSS
    let styleElement = document.getElementById('tenant-theme-css') as HTMLStyleElement | null;

    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = 'tenant-theme-css';
      // Insert after index.css so tenant CSS overrides it
      document.head.appendChild(styleElement);
    }

    styleElement.textContent = themeCSS;

    console.log('[TenantBranding] Tenant theme CSS applied');

    // Cleanup on unmount
    return () => {
      const existingStyle = document.getElementById('tenant-theme-css');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [themeCSS]);

  // This component doesn't render anything
  return null;
}
